/**
 * SearchableMultiSelect — a dropdown-with-search multi-select control.
 *
 * Looks like a normal <select>: a control showing the current selection (as
 * removable chips) that opens a panel with an integrated filter box and a
 * checkbox list. Modelled on the single-select SaaS picker in AppModal so the
 * two read the same.
 *
 * Values not present in `options` (e.g. an external-IdP group not in the local
 * directory, kept when editing a rule) still render as chips and stay selected,
 * so a selection is never silently dropped.
 *
 * React-Compiler-safe: the outside-click listener is attached on a real DOM
 * event in an effect, never setState-in-render or ref-in-render.
 */

import { useEffect, useRef, useState } from 'react';
import { ChevronDown, X } from 'lucide-react';

export interface MultiSelectOption {
  value: string;
  label: string;
  /** Small right-aligned hint (e.g. member count, app type, email). */
  hint?: string;
}

interface SearchableMultiSelectProps {
  options: MultiSelectOption[];
  selected: string[];
  onChange: (next: string[]) => void;
  id?: string;
  placeholder?: string;
  searchPlaceholder?: string;
  /** Shown in the panel when there are no options at all. */
  emptyText?: string;
}

const SearchableMultiSelect = ({
  options,
  selected,
  onChange,
  id,
  placeholder = 'Select…',
  searchPlaceholder = 'Type to filter…',
  emptyText = 'Nothing to choose from.',
}: SearchableMultiSelectProps) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  const labelFor = (value: string) => options.find((o) => o.value === value)?.label ?? value;

  const q = query.trim().toLowerCase();
  const filtered = q
    ? options.filter((o) => o.label.toLowerCase().includes(q) || o.value.toLowerCase().includes(q))
    : options;

  const toggle = (value: string) =>
    onChange(selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value]);

  return (
    <div className="relative" ref={boxRef}>
      <button
        type="button"
        id={id}
        className="input-base flex w-full items-center justify-between gap-2 text-left min-h-[38px]"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        {selected.length === 0 ? (
          <span className="text-gray-400">{placeholder}</span>
        ) : (
          <span className="flex flex-wrap gap-1">
            {selected.map((v) => (
              <span
                key={v}
                className="inline-flex items-center gap-1 rounded bg-primary-50 border border-primary-200 px-1.5 py-0.5 text-xs text-primary-700"
              >
                {labelFor(v)}
                <X
                  className="h-3 w-3 cursor-pointer hover:text-primary-900"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggle(v);
                  }}
                />
              </span>
            ))}
          </span>
        )}
        <ChevronDown className={`h-4 w-4 shrink-0 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute z-20 mt-1 w-full rounded-md border border-gray-200 bg-white shadow-lg">
          <div className="border-b border-gray-100 p-2">
            <input
              type="text"
              autoFocus
              className="input-base w-full"
              placeholder={searchPlaceholder}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <ul className="max-h-48 overflow-auto py-1" role="listbox" aria-multiselectable="true">
            {options.length === 0 ? (
              <li className="px-3 py-2 text-sm text-gray-400">{emptyText}</li>
            ) : filtered.length === 0 ? (
              <li className="px-3 py-2 text-sm text-gray-400">No matches.</li>
            ) : (
              filtered.map((o) => {
                const active = selected.includes(o.value);
                return (
                  <li key={o.value} role="option" aria-selected={active}>
                    <label
                      className={`flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-left text-sm hover:bg-blue-50 ${
                        active ? 'bg-blue-50 font-medium text-blue-700' : 'text-gray-700'
                      }`}
                    >
                      <input type="checkbox" checked={active} onChange={() => toggle(o.value)} />
                      <span className="flex-1 truncate">{o.label}</span>
                      {o.hint && <span className="text-xs text-gray-400 shrink-0">{o.hint}</span>}
                    </label>
                  </li>
                );
              })
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default SearchableMultiSelect;
